import type { ServerMessage } from '@vitals/shared';
import type { ClientStore } from '../state/clientStore.js';
import type { ServerClock } from '../state/clock.js';
import type { GeometryEpochSink } from '../state/geometryEpochHub.js';

/**
 * How recently the client must have applied a server message for a (re)connect
 * to skip the server's multi-MB on-connect snapshot via ?nosnap=1.
 *
 * The edge-cached /snapshot itself can now be up to 30s stale on a cache HIT
 * (s-maxage=30, SWR=120) — what keeps this 15s threshold safe is NOT TTL
 * matching, but that `bootstrapSnapshot` Age-corrects the clock sync
 * (`ServerClock.sync(serverTime + ageMs, ageMs)`, see clock.ts), backdating
 * `syncedAt` by the response's actual cache Age. So a 30s-stale HIT reports
 * `msSinceLastSync() >= 30_000` immediately, which is already well past this
 * threshold — it correctly fails the freshness check and takes the full
 * snapshot rather than nosnap=1, regardless of how the edge TTL is tuned.
 *
 * Accepted tradeoff: a layer that updates during a sub-15s disconnect gap is
 * missed by the skipped snapshot. Every server poller re-broadcasts its layer
 * on each successful poll (StateStore.set has no dedup), so the miss self-heals
 * within one poll interval — seconds for transit layers, a few minutes for the
 * slowest ambient ones. Eliminating the gap entirely would need sequence
 * numbers + server-side replay; not worth it for that exposure.
 */
export const NOSNAP_MAX_AGE_MS = 15_000;

/**
 * Browsers never expose protocol-level WS ping/pong, so client-side liveness
 * is inferred from application traffic instead: if no message (snapshot,
 * delta, or hello) arrives within this window, the socket is presumed dead
 * (the common case is a backgrounded/throttled mobile tab whose TCP session
 * silently dies) and force-closed so the existing reconnect/backoff path in
 * `onclose` takes over — there is deliberately no second reconnect mechanism.
 *
 * Sized against the real broadcast cadence: the subway poller ticks
 * unconditionally every `subwayPollMs` (config-clamped floor 10s, default
 * 15s) and `StateStore.set` never dedups, so a connected client receives at
 * least one delta roughly every 15s whenever the server is healthy. 60s is a
 * ~4x margin above that — generous enough to absorb a slow tick or a
 * coalesced flush without false-triggering on a healthy connection, while
 * still catching a dead socket well before a user would notice frozen data.
 */
export const WATCHDOG_MS = 60_000;

/** Append ?nosnap=1 when the client already holds fresh state. */
export function wsUrlFor(url: string, hasFreshState: boolean): string {
  if (!hasFreshState) return url;
  return `${url}${url.includes('?') ? '&' : '?'}nosnap=1`;
}

/**
 * True when the runtime can inflate a gzip byte stream (DecompressionStream),
 * gating the `gz=1` broadcast opt-in. perMessageDeflate stays off (per-client
 * compression CPU is the scarce resource server-side) — this instead lets the
 * server compress each broadcast ONCE and share the same bytes across every
 * gz-capable client, at the cost of client-side inflate (cheap, browser-native).
 */
export function supportsGzipBroadcasts(): boolean {
  return typeof DecompressionStream !== 'undefined';
}

/** Append ?gz=1 when the runtime supports inflating gzip broadcast frames. */
export function withGzParam(url: string, wantsGz: boolean): string {
  if (!wantsGz) return url;
  return `${url}${url.includes('?') ? '&' : '?'}gz=1`;
}

/**
 * Default inflate: browser/Node DecompressionStream. Injectable for tests.
 * Routed through `Response` (not `Blob`) — `Blob.prototype.stream()` isn't
 * implemented in every DOM-shim test environment, while `Response` bodies are.
 */
async function inflateGzip(buf: ArrayBuffer): Promise<string> {
  const body = new Response(buf).body;
  if (body === null) throw new Error('gzip frame produced no readable body');
  const stream = body.pipeThrough(new DecompressionStream('gzip'));
  return new Response(stream).text();
}

/** Decode a binary WS frame (gzip-compressed JSON text) into a ServerMessage. */
export async function decodeGzipFrame(
  buf: ArrayBuffer,
  inflate: (buf: ArrayBuffer) => Promise<string> = inflateGzip,
): Promise<ServerMessage> {
  const text = await inflate(buf);
  return JSON.parse(text) as ServerMessage;
}

export function connectWs(opts: {
  url: string;
  store: ClientStore;
  clock: ServerClock;
  /** Injectable for tests; defaults to the browser WebSocket. */
  wsFactory?: (url: string) => WebSocket;
  /** Injectable for tests; defaults to DecompressionStream-based inflate. */
  inflate?: (buf: ArrayBuffer) => Promise<string>;
  /** Injectable for tests; defaults to Math.random. Used only for reconnect jitter. */
  rng?: () => number;
  /**
   * Receives the (untrusted, unvalidated) `geometry` epochs field whenever a
   * hello or snapshot carries one, so stale drawn geometry can be refetched.
   */
  geometryEpochs?: GeometryEpochSink;
}): void {
  const wsFactory = opts.wsFactory ?? ((url: string) => new WebSocket(url));
  const rng = opts.rng ?? Math.random;
  const wantsGz = supportsGzipBroadcasts();
  let retry = 1000;
  const open = () => {
    // The clock syncs on every applied server message (WS or /snapshot
    // bootstrap), so a recent sync means the store is fresh enough to skip the
    // on-connect snapshot and just resume the delta stream. Cold loads and
    // long disconnects still get the full snapshot.
    const sinceSync = opts.clock.msSinceLastSync();
    const hasFreshState = sinceSync !== null && sinceSync < NOSNAP_MAX_AGE_MS;
    const ws = wsFactory(withGzParam(wsUrlFor(opts.url, hasFreshState), wantsGz));
    ws.binaryType = 'arraybuffer';

    // Serializes message handling (including async gzip decode) in arrival
    // order per socket — without this, a small delta could finish decoding
    // before an earlier, larger snapshot still inflating, and overtake it.
    let decodeChain: Promise<void> = Promise.resolve();

    // Liveness watchdog (see WATCHDOG_MS): reset on every message received,
    // cleared on close so a stale timer from this socket can never fire
    // against a later reconnect.
    let watchdogHandle: ReturnType<typeof setTimeout> | null = null;
    const clearWatchdog = (): void => {
      if (watchdogHandle !== null) {
        clearTimeout(watchdogHandle);
        watchdogHandle = null;
      }
    };
    const resetWatchdog = (): void => {
      clearWatchdog();
      watchdogHandle = setTimeout(() => {
        console.warn('[ws] no messages received within watchdog window, reconnecting');
        ws.close();
      }, WATCHDOG_MS);
    };

    const applyMessage = (msg: ServerMessage, fromBinaryFrame: boolean): void => {
      // Hello and snapshot may carry geometry epochs (absent from old servers).
      // Epochs are content hashes, never time-sensitive, so even a shared/cached
      // gz snapshot's copy is safe to forward; the hub validates the shape.
      if ((msg.type === 'hello' || msg.type === 'snapshot') && msg.geometry !== undefined) {
        opts.geometryEpochs?.announce(msg.geometry);
      }
      if (msg.type === 'hello') {
        opts.clock.sync(msg.serverTime);
        return;
      }
      if (msg.type === 'snapshot') {
        // A gz snapshot buffer is compressed once server-side and shared
        // across connections/time, so its embedded serverTime can be stale;
        // gz clients already synced their clock from the preceding 'hello'.
        if (!fromBinaryFrame) opts.clock.sync(msg.serverTime);
        msg.states.forEach((s) => opts.store.apply(s));
        return;
      }
      // Deltas are generated fresh per flush (never cached/replayed), so
      // their serverTime is always current — safe to sync from, binary or not.
      opts.clock.sync(msg.serverTime);
      opts.store.apply(msg.state);
    };

    ws.onmessage = (ev) => {
      resetWatchdog();
      decodeChain = decodeChain
        .then(async () => {
          if (ev.data instanceof ArrayBuffer) {
            const msg = await decodeGzipFrame(ev.data, opts.inflate);
            applyMessage(msg, true);
            return;
          }
          let msg: ServerMessage;
          try {
            msg = JSON.parse(ev.data as string) as ServerMessage;
          } catch (err) {
            // Drop a malformed frame rather than killing the handler; socket stays open.
            console.warn('[ws] ignoring malformed message', err);
            return;
          }
          applyMessage(msg, false);
        })
        .catch((err: unknown) => {
          // A binary decode failure (corrupt gzip stream) means this socket's
          // byte stream is no longer trustworthy — reconnect fresh rather than
          // silently skipping the frame.
          console.warn('[ws] failed to decode binary message, reconnecting', err);
          ws.close();
        });
    };
    ws.onopen = () => { retry = 1000; resetWatchdog(); };
    ws.onclose = () => {
      clearWatchdog();
      // Jitter the actual delay (not the stored `retry` backoff value) so a
      // server deploy that drops every client at once doesn't reconnect them
      // all in lockstep — a synchronized thundering-herd reconnect storm
      // right as the server comes back up. Factor in [0.5, 1.5); the cap
      // semantics on `retry` itself are unchanged, jitter just spreads out
      // the already-capped delay.
      const jitterFactor = 0.5 + rng();
      setTimeout(open, retry * jitterFactor);
      retry = Math.min(retry * 2, 15000);
    };
    ws.onerror = () => ws.close();
  };
  open();
}
